import ActionType from '@/Common/Enum/ActionType.js';
import nightcrawler, {
  NIGHTCRAWLER_CHECK_STATUS,
  onlyFailing
} from '@/Common/Classifiers/NightcrawlerCheckClassifier.js';
import i18n from '@/Plugins/i18n.js';

const t = (key) => i18n.global.t(`common.spf_directive.${key}`);

export default nightcrawler({
  _root: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPF001':
              return t('spf001');
            case 'SPF002':
              return t('spf002');
            case 'SPF003':
              return t('spf003');
            case 'SPF004':
              return t('spf004');
            case 'SPF005':
            case 'SPF012':
            case 'SPF013':
            default:
              return c.msg;
          }
        })
    }
  ],
  v: [
    {
      default: true,
      required: true,
      actionType: ActionType.ACTION_REQUIRED,
      message: t('v_not_specified')
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('v_first_directive')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  a: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFA01':
              return t('a_invalid_domain');
            case 'SPFA02':
              return t('a_record_not_exist');
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  mx: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFMX01':
              return t('mx_invalid_domain');
            case 'SPFMX02':
              return t('mx_record_not_exist');
            case 'SPFMX03':
              return t('mx_invalid_mail_servers');
            case 'SPFMX04':
              return t('mx_mail_servers_a_not_exist');
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  include: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFINCLUDE01':
              return t('include_invalid_domain_or_macro');
            case 'SPFINCLUDE02':
              return t('include_record_not_exist', {
                detail: c.msg.replace('Include record exists', '')
              });
            case 'SPFINCLUDE03':
              return t('include_macro_invalid', {
                detail: c.msg.replace('Include macro is valid', '')
              });
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  redirect: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFREDIRECT01':
              return t('redirect_no_directives_after');
            case 'SPFREDIRECT02':
              return t('redirect_separator_equals');
            case 'SPFREDIRECT03':
              return t('redirect_invalid_domain');
            case 'SPFREDIRECT04':
              return t('redirect_record_not_exist', {
                detail: c.msg.replace('Redirect record exists', '')
              });
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  exists: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFEXISTS01':
              return t('exists_not_macro');
            case 'SPFEXISTS02':
              return t('exists_macro_invalid', {
                detail: c.msg.replace('Exists macro is valid', '')
              });
            case 'SPFEXISTS03':
              return t('exists_behaves_like_all');
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  ip4: [
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('ip4_invalid')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  ip6: [
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('ip6_invalid')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  exp: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFEXP01':
              return t('exp_appears_before_all');
            case 'SPFEXP02':
              return t('exp_separator_equals');
            case 'SPFEXP03':
              return t('exp_invalid_domain_or_macro');
            case 'SPFEXP04':
              return t('exp_macro_invalid', {
                detail: c.msg.replace('Exp macro is valid', '')
              });
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  ptr: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFPTR01':
              return t('ptr_invalid_domain');
            case 'SPFPTR02':
              return t('ptr_expensive_deprecated');
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION,
      errorMessages: [t('ptr_expensive_deprecated')]
    }
  ],
  ra: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFRA01':
              return t('ra_invalid_address');
            case 'SPFPTR02':
              return t('ra_should_be_in_root');
            case 'SPFRA03':
              return t('ra_obsoleted_by_dmarc');
          }
        })
    }
  ],
  rp: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFRP01':
              return t('rp_invalid_percentage');
            case 'SPFRP02':
              return t('rp_obsoleted_by_dmarc');
          }
        })
    }
  ],
  rr: [
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFRR01':
              return t('rr_invalid_requested_report');
            case 'SPFRR02':
              return t('rr_obsoleted_by_dmarc');
          }
        })
    }
  ],
  all: [
    {
      default: true,
      required: true,
      actionType: ActionType.ACTION_REQUIRED,
      message: t('all_not_specified')
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'SPFALL01':
              return t('all_should_have_no_value');
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ]
});
